import { Component, Input, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';

@Component({
  selector: 'app-file-preview-modal',
  standalone: true,
  imports: [
    NgxDocViewerModule, 
    CommonModule,
    NgxExtendedPdfViewerModule
  ],
  templateUrl: './file-preview-modal.component.html',
  styleUrls: ['./file-preview-modal.component.scss']
})
export class FilePreviewModalComponent implements OnDestroy {
  @Input() set fileUrl(value: string | SafeResourceUrl) {
    this._fileUrl = value;
    this.initializeViewer();
  }
  get fileUrl(): string | SafeResourceUrl {
    return this._fileUrl;
  }
  private _fileUrl!: string | SafeResourceUrl;

  @Input() set fileName(value: string) {
    this._fileName = value;
    this.initializeViewer();
  }
  get fileName(): string {
    return this._fileName;
  }
  private _fileName = '';

  @Input() set fileType(value: string) {
    this._fileType = value;
    this.initializeViewer();
  }
  get fileType(): string {
    return this._fileType;
  }
  private _fileType = '';

  displayUrl: string = ''; // URL à afficher
  safeUrl: SafeResourceUrl;
  useEmbed = false;
  useGoogleViewer = false;
  isImage = false;
  isUnsupported = false;
  sanitizedGoogleViewerUrl: SafeResourceUrl | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private sanitizer: DomSanitizer
  ) {
    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl('');
  }

  initializeViewer() {
    if (!this._fileUrl) {
      return;
    }

    // Convertir SafeResourceUrl en string si nécessaire
    const urlString = typeof this._fileUrl === 'string'
      ? this._fileUrl
      : this._fileUrl.toString();

    this.displayUrl = urlString;
    const googleDocsUrl = `https://docs.google.com/gview?url=${encodeURIComponent(this.displayUrl)}&embedded=true`;
    this.sanitizedGoogleViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(googleDocsUrl);


    // Détection du type de fichier
    const extension = this.getFileExtension(this._fileName || urlString);
    const mimeType = this._fileType.toLowerCase();

    this.isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)
      || mimeType.startsWith('image/');
    this.useGoogleViewer = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)
      || [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ].includes(mimeType);
    this.useEmbed = ['pdf'].includes(extension) || mimeType === 'application/pdf';
    this.isUnsupported = !this.isImage && !this.useGoogleViewer && !this.useEmbed;

    // Préparer l'URL sécurisée
    if (this.isImage || this.useEmbed) {
      this.safeUrl = typeof this._fileUrl === 'string'
        ? this.sanitizer.bypassSecurityTrustResourceUrl(this._fileUrl)
        : this._fileUrl;
    }
  }

  private getFileExtension(urlOrFilename: string): string {
    const filename = urlOrFilename.split('/').pop() || '';
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  ngOnDestroy() {
    // Nettoyage des URLs blob
    if (typeof this._fileUrl === 'string' && this._fileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this._fileUrl);
    }
  }
}
